import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { shallowEqual, useSelector } from 'react-redux';

import { CheckSelect, InputField, SelectOptions, SelectWithSearch } from 'components/ui/controls/SearchTickets';
import { RegularWrapper } from 'components/common';
import { Wrapper, Title, MiddleBlock, BottomBlock, AddPersonBlock, InputWrapper } from './Common.styles';
import { getSortedAlphabetOptions } from 'reactStore/selectors/externalNationalityCodesSelector';

const GENDER_OPTIONS = [
  {
    title: 'Жен.',
    value: 'F',
  },
  {
    title: 'Муж.',
    value: 'M',
  },
];

const Common = ({
  value,
  onChange,
  errors,
  isGuest,
  isAddEmployeePage,
  checkSelectedOptionsUser,
  setCheckSelectedOptionsUser,
  checkSelectedOptionsContactPerson,
  setCheckSelectedOptionsContactPerson,
  checkSelectedOptionsTraveller,
  setCheckSelectedOptionsTraveller,
}) => {
  const { t } = useTranslation();
  const [roleOptions, setRoleOptions] = useState([]);
  const sortedAlphabetOptions = useSelector(getSortedAlphabetOptions, shallowEqual);
  useEffect(() => {
    if (checkSelectedOptionsUser.includes('user')) {
      setRoleOptions([
        {
          title: 'Self-booker',
          value: 'self_booker',
        },
        {
          title: 'Менеджер',
          value: 'manager',
        },
      ]);
      onChangeField('role')('self_booker');
    } else if (isGuest) {
      setRoleOptions([
        {
          title: 'Гость',
          value: 'guest',
        },
      ]);
      onChangeField('role')('guest');
    } else {
      setRoleOptions([
        {
          title: 'Сотрудник',
          value: 'employee',
        },
      ]);
      onChangeField('role')('employee');
    }
  }, [isAddEmployeePage, checkSelectedOptionsUser, isGuest]);
  const [currentValue] = value;

  useEffect(() => {
    if (!currentValue.role) {
      onChangeField('role')(roleOptions?.[0]?.value);
    }
  }, [currentValue, roleOptions]);

  const onChangeField = (field) => (e) => {
    const currentValue = e?.target ? e.target.value : e;

    onChange([{ ...value[0], [field]: currentValue?.trim() }]);
  };

  const setOptions = (field) => {
    const options = {
      user: {
        state: checkSelectedOptionsUser,
        func: setCheckSelectedOptionsUser,
      },
      contactPerson: {
        state: checkSelectedOptionsContactPerson,
        func: setCheckSelectedOptionsContactPerson,
      },
      canTravelling: {
        state: checkSelectedOptionsTraveller,
        func: setCheckSelectedOptionsTraveller,
      },
    };
    if (!options[field]?.state?.length) {
      options[field]?.func([field]);
    } else {
      options[field]?.func([]);
    }
  };

  const [error] = errors;

  return (
    <Wrapper>
      <Title>{t('Settings.Person.Card.Common')}</Title>
      <MiddleBlock>
        <RegularWrapper gap="12px">
          <InputWrapper width="260px">
            <InputField
              label={t('Settings.Person.Card.LastName')}
              placeholder="Иванов"
              onChange={onChangeField('last_name')}
              value={currentValue?.last_name}
              error={error?.last_name}
            />
          </InputWrapper>
          <InputWrapper>
            <InputField
              label={t('Settings.Person.Card.FirstName')}
              placeholder="Иван"
              onChange={onChangeField('first_name')}
              value={currentValue?.first_name}
              error={error?.first_name}
            />
          </InputWrapper>
          <InputWrapper>
            <InputField
              label={t('Settings.Person.Card.MiddleName')}
              placeholder="Иванович"
              onChange={onChangeField('middle_name')}
              value={currentValue?.middle_name}
              error={error?.middle_name}
            />
          </InputWrapper>
        </RegularWrapper>
      </MiddleBlock>
      <BottomBlock>
        <RegularWrapper gap="12px">
          <InputWrapper width="100px">
            <SelectOptions
              label={t('Settings.Person.Card.Gender')}
              options={GENDER_OPTIONS}
              value={currentValue?.gender}
              error={error?.gender}
              onChange={onChangeField('gender')}
            />
          </InputWrapper>
          <InputWrapper width="148px">
            <InputField
              label={t('Settings.Person.Card.DateOfBirth')}
              mask="99.99.9999"
              placeholder="01.08.2000"
              value={currentValue?.date_of_birth}
              error={error?.date_of_birth}
              onChange={onChangeField('date_of_birth')}
            />
          </InputWrapper>
          <InputWrapper>
            <SelectWithSearch
              label={t('Settings.Person.Card.Nationality')}
              options={sortedAlphabetOptions}
              onChange={onChangeField('nationality_code_id')}
              value={currentValue?.nationality_code_id}
              error={error?.nationality_code_id}
            />
          </InputWrapper>
        </RegularWrapper>
      </BottomBlock>
      <AddPersonBlock>
        <RegularWrapper gap="12px" align="center">
          <CheckSelect
            items={[{ title: 'Пользователь', value: 'user' }]}
            currentValue={checkSelectedOptionsUser}
            onChange={() => setOptions('user')}
          />
          {!!checkSelectedOptionsUser.length && (
            <InputWrapper width="260px" marginLeft="38px">
              <InputField
                onChange={onChangeField('email')}
                error={error?.email}
                value={currentValue?.email}
                label="Почта"
                placeholder="person@mail.ru"
              />
            </InputWrapper>
          )}
        </RegularWrapper>
        <RegularWrapper gap="12px" margin="20px 0 0 0" align="center">
          <CheckSelect
            items={[{ title: 'Контактное лицо', value: 'contactPerson' }]}
            currentValue={checkSelectedOptionsContactPerson}
            onChange={() => setOptions('contactPerson')}
          />
          {!!checkSelectedOptionsContactPerson.length && (
            <InputWrapper width="260px">
              <InputField
                onChange={onChangeField('contact_email')}
                error={error?.contact_email}
                value={currentValue?.contact_email}
                label="Контактная почта"
                placeholder="contact-person@mail.ru"
              />
            </InputWrapper>
          )}
        </RegularWrapper>
        <RegularWrapper gap="12px" margin="20px 0 0 0" align="center">
          <CheckSelect
            items={[{ title: 'Traveller', value: 'canTravelling' }]}
            currentValue={checkSelectedOptionsTraveller}
            onChange={() => setOptions('canTravelling')}
          />
        </RegularWrapper>
        {(checkSelectedOptionsUser.includes('user') || (checkSelectedOptionsUser.includes('user') && isGuest)) && (
          <RegularWrapper gap="12px" margin="20px 0 0 0" width="260px">
            <SelectOptions
              onChange={onChangeField('role')}
              options={roleOptions}
              value={currentValue?.role ? currentValue?.role : roleOptions[0]?.value}
              error={error?.role}
              label="Роль"
              disabled={!checkSelectedOptionsUser.length}
            />
          </RegularWrapper>
        )}
      </AddPersonBlock>
    </Wrapper>
  );
};

Common.propTypes = {
  isGuest: PropTypes.bool,
  isAddEmployeePage: PropTypes.bool,
  onChange: PropTypes.func,
  setCheckSelectedOptionsUser: PropTypes.func,
  setCheckSelectedOptionsContactPerson: PropTypes.func,
  setCheckSelectedOptionsTraveller: PropTypes.func,
  checkSelectedOptionsContactPerson: PropTypes.array,
  checkSelectedOptionsUser: PropTypes.array,
  checkSelectedOptionsTraveller: PropTypes.array,
  value: PropTypes.array,
  errors: PropTypes.array,
};

export default Common;
